import { useEffect, useState } from 'react';

export type AppMode = 'office' | 'web';

export interface OfficeContext {
  mode: AppMode;
  host: 'PowerPoint' | 'Word' | 'Excel' | 'Outlook' | 'unknown';
  initialized: boolean;
  error: string | null;
}

// If Office.onReady doesn't resolve within this window we assume we're running
// in a plain browser tab (slides.trackyour.app directly), not inside an Office
// host. The Office task pane normally fires onReady within tens of milliseconds;
// 1.5s is generous.
const OFFICE_READY_TIMEOUT_MS = 1500;

// `Office` (the JS library) can be present on standalone pages too — for example
// if the manifest's CDN script tag is included on a plain page. That alone does
// NOT mean we're running inside a real Office host. What distinguishes a real
// task pane is the presence of `OfficeRuntime.auth.getAccessToken`, which is
// the SSO bridge that only exists inside the actual add-in iframe. So the
// authoritative office-mode check is for OfficeRuntime, not Office.
function isInOfficeHost(): boolean {
  const OR: any = (window as any).OfficeRuntime;
  return !!(OR && OR.auth && typeof OR.auth.getAccessToken === 'function');
}

function detectHostAndMode(): Promise<{ mode: AppMode; host: OfficeContext['host'] }> {
  return new Promise((resolve) => {
    const Office: any = (window as any).Office;
    if (typeof Office === 'undefined' || typeof Office.onReady !== 'function') {
      resolve({ mode: 'web', host: 'unknown' });
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve({ mode: 'web', host: 'unknown' });
    }, OFFICE_READY_TIMEOUT_MS);

    Office.onReady(() => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      // Office.js loaded — but are we actually inside an Office host? Without
      // OfficeRuntime there's no SSO bridge, so even if Office.onReady fires
      // we treat this as a plain browser tab.
      if (!isInOfficeHost()) {
        resolve({ mode: 'web', host: 'unknown' });
        return;
      }

      const hostEnum = Office?.context?.host;
      const host =
        (Object.keys(Office?.HostType || {}).find(
          (k) => Office.HostType[k] === hostEnum
        ) as any) || 'unknown';
      resolve({ mode: 'office', host });
    });
  });
}

export function useOfficeContext(): OfficeContext {
  const [state, setState] = useState<OfficeContext>({
    mode: 'web',
    host: 'unknown',
    initialized: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    detectHostAndMode()
      .then(({ mode, host }) => {
        if (cancelled) return;
        setState({ mode, host, initialized: true, error: null });
      })
      .catch((e: any) => {
        if (cancelled) return;
        setState({
          mode: 'web',
          host: 'unknown',
          initialized: true,
          error: e?.message || 'Host detection failed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
